import { postToNetlifyFunction, readNetlifyErrorMessage } from './netlifyClient';

const toUint8ArrayFromBase64Url = (base64Url: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
};

const getPushPublicKey = (): string => {
  return (process.env.REACT_APP_WEB_PUSH_VAPID_PUBLIC_KEY || '').trim();
};

class WebPushSubscriptionService {
  private getPersistenceErrorMessage(status: number): string {
    return `Unable to save push subscription (${status})`;
  }

  private shouldSkipSubscriptionSync(): boolean {
    if (typeof window === 'undefined') return true;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return true;
    if (Notification.permission !== 'granted') return true;
    return !getPushPublicKey();
  }

  private async getOrCreateSubscription(
    vapidPublicKey: string
  ): Promise<PushSubscription> {
    const registration = await navigator.serviceWorker.ready;
    const existingSubscription = await registration.pushManager.getSubscription();

    if (existingSubscription) {
      return existingSubscription;
    }

    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: toUint8ArrayFromBase64Url(vapidPublicKey),
    });
  }

  private async persistSubscription(subscription: PushSubscription): Promise<'subscribed' | 'skipped'> {
    const response = await postToNetlifyFunction({
      functionName: 'push-subscriptions',
      missingTokenStrategy: 'skip',
      payload: {
        subscription: subscription.toJSON(),
      },
    });

    if (!response) {
      return 'skipped';
    }

    if (!response.ok) {
      const fallbackMessage = this.getPersistenceErrorMessage(response.status);
      const detail = await readNetlifyErrorMessage(
        response,
        fallbackMessage
      );
      throw new Error(`${fallbackMessage}: ${detail}`);
    }

    return 'subscribed';
  }

  public async syncCurrentSubscription(): Promise<'subscribed' | 'skipped'> {
    if (this.shouldSkipSubscriptionSync()) {
      return 'skipped';
    }

    const subscription = await this.getOrCreateSubscription(getPushPublicKey());
    return this.persistSubscription(subscription);
  }
}

export const webPushSubscriptionService = new WebPushSubscriptionService();
